<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-select-input
                col="4"
                :placeholder="$t('PLACEHOLDERS.order_type')"
                v-model="filterOptions.order_type"
                :optionsList="orderTypeOptions"
              />

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_from')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_to')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.financial_reports") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcel"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="displayRows"
        item-class="ltr"
        :items-per-page="-1"
        hide-default-footer
      >
        <template v-slot:[`item.serial_number`]="{ item, index }">
          {{
            item?.is_totals
              ? "-"
              : (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
          }}
        </template>
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <template v-slot:[`item.serial_number_value`]="{ item }">
          <span v-if="item?.is_totals" class="fw-bold">{{ $t("PLACEHOLDERS.totals") }}</span>
          <span v-else>{{ item.serial_number || "-" }}</span>
        </template>

        <template v-slot:[`item.user_name`]="{ item }">
          <span v-if="item?.is_totals">-</span>
          <span v-else>{{ item.user_name || "-" }}</span>
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          <span v-if="item?.is_totals">-</span>
          <span v-else>{{ formatDate(item.created_at) }}</span>
        </template>

        <template v-slot:[`item.order_type`]="{ item }">
          <span v-if="item?.is_totals">-</span>
          <span v-else>
            <span v-if="item.order_type === 'delivery'">{{ $t("PLACEHOLDERS.delivery") }}</span>
            <span v-else-if="item.order_type === 'pickup'">{{ $t("PLACEHOLDERS.pickup") }}</span>
            <span v-else>{{ item.order_type || "-" }}</span>
          </span>
        </template>

        <template v-slot:[`item.representative_profit`]="{ item }">
          <span :class="{ 'fw-bold': item?.is_totals }">
            {{ formatCurrency(item.representative_profit) }}
          </span>
        </template>

        <template v-slot:[`item.print_shop_profit`]="{ item }">
          <span :class="{ 'fw-bold': item?.is_totals }">
            {{ formatCurrency(item.print_shop_profit) }}
          </span>
        </template>

        <template v-slot:[`item.application_profit`]="{ item }">
          <span :class="{ 'fw-bold': item?.is_totals }">
            {{ formatCurrency(item.application_profit) }}
          </span>
        </template>

        <template v-slot:[`item.total_profit`]="{ item }">
          <span :class="{ 'fw-bold': item?.is_totals }">
            {{ formatCurrency(item.total_profit) }}
          </span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div v-if="!item?.is_totals" class="actions">
            <button
              class="btn_show"
              type="button"
              @click="$router.push({ name: 'Showorders', params: { id: item.id } })"
            >
              <i class="fal fa-eye"></i>
            </button>
          </div>
          <span v-else>-</span>
        </template>
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!-- ==== Start:: Overall_statistics Addresses ==== -->
      <!-- <div class="table_content mt-5">
        <v-simple-table class="stat-table">
          <template v-slot:default>
            <thead class="all-stat">
              <tr>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.total_subscription_value") }}
                </th>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.total_tax_amount") }}
                </th>
                <th class="text-center" style="color: white !important">
                  {{ $t("PLACEHOLDERS.subscription_total") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center">
                <td>{{ totals.total_price }}</td>
                <td>{{ totals.total_tax }}</td>
                <td>{{ totals.total_amount }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div> -->
      <!-- ==== End:: Overall_statistics Addresses ==== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Generate PDF Template Content -->
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="$t('PLACEHOLDERS.financial_reports')"
      :pdf-quality="2"
      pdf-format="a4"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      @hasGenerated="$message.success($t('MESSAGES.generatedSuccessfully'))"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">
            {{ $t("PLACEHOLDERS.financial_reports") }}
          </h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th
                    v-for="(header, index) in tableHeaders"
                    :key="header.value"
                  >
                    <span>{{ header.text }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in displayRows" :key="`${row.id || 't'}-${index}`" :style="row.is_totals ? 'font-weight:700' : ''">
                  <td>
                    {{
                      row.is_totals
                        ? "-"
                        : (paginations.current_page - 1) *
                            paginations.items_per_page +
                          index +
                          1
                    }}
                  </td>
                  <td>{{ row.is_totals ? $t("PLACEHOLDERS.totals") : (row.serial_number || '-') }}</td>
                  <td>{{ row.is_totals ? '-' : (row.user_name || '-') }}</td>
                  <td>{{ row.is_totals ? '-' : (row.created_at || '-') }}</td>
                  <td>{{ row.is_totals ? '-' : (row.order_type === 'delivery' ? $t("PLACEHOLDERS.delivery") : row.order_type === 'pickup' ? $t("PLACEHOLDERS.pickup") : (row.order_type || '-')) }}</td>
                  <td>{{ formatCurrency(row.representative_profit) }}</td>
                  <td>{{ formatCurrency(row.print_shop_profit) }}</td>
                  <td>{{ formatCurrency(row.application_profit) }}</td>
                  <td>{{ formatCurrency(row.total_profit) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <!-- ==== Start:: Overall_statistics Addresses ==== -->
          <!-- <div class="table_content mt-5">
            <v-simple-table class="stat-table">
              <template v-slot:default>
                <thead class="all-stat">
                  <tr>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_subscription_value") }}
                    </th>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_tax_amount") }}
                    </th>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.subscription_total") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="text-center">
                    <td>{{ totals.total_price }}</td>
                    <td>{{ totals.total_tax }}</td>
                    <td>{{ totals.total_amount }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div> -->
          <!-- ==== End:: Overall_statistics Addresses ==== -->
        </div>
      </section>
    </vue-html2pdf>
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";
export default {
  name: "AllFinancialReports",

  components: {
    VueHtml2pdf,
    },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    orderTypeOptions() {
      return [
        { id: 0, name: this.$t("all"), value: null },
        { id: 1, name: this.$t("PLACEHOLDERS.delivery"), value: "delivery" },
        { id: 2, name: this.$t("PLACEHOLDERS.pickup"), value: "pickup" },
      ];
    },
    displayRows() {
      const rows = Array.isArray(this.tableRows) ? this.tableRows : [];
      if (!rows.length) return [];
      return [
        ...rows,
        {
          is_totals: true,
          representative_profit: this.totals.representative_profit || 0,
          print_shop_profit: this.totals.print_shop_profit || 0,
          application_profit: this.totals.application_profit || 0,
          total_profit: this.totals.total_profit || 0,
        },
      ];
    },
  },

  data() {
    return {
      isWaitingRequest: false,
      excelDownloadBtnIsLoading: false,
      loading: false,
      filterFormIsActive: false,
      filterOptions: {
        order_type: null,
        from_date: null,
        to_date: null,
      },
      tableHeaders: [
        {
          text: this.$t("TABLES.Rates.serialNumber"),
          value: "serial_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNum"),
          value: "serial_number_value",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.clientName"),
          value: "user_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("SIDENAV.orders.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.order_type"),
          value: "order_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.representative_profit"),
          value: "representative_profit",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.print_shop_profit"),
          value: "print_shop_profit",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.application_profit"),
          value: "application_profit",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_profit"),
          value: "total_profit",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      totals: {
        representative_profit: 0,
        print_shop_profit: 0,
        application_profit: 0,
        total_profit: 0,
      },
    };
  },

  methods: {
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };

      return date.toLocaleString(this.getAppLocale === "ar" ? "ar-EG" : "en-US", options);
    },
    localizedText(value) {
      if (value == null || value === "") return "";
      const locale = this.getAppLocale || "ar";

      // If API already returns a plain string
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return "";

        // Try parse JSON string e.g. {"ar":"..","en":".."}
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
          try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === "object") {
              return (
                parsed?.[locale] ??
                parsed?.ar ??
                parsed?.en ??
                Object.values(parsed)[0] ??
                ""
              );
            }
          } catch (_) {
            // Not JSON, fall back to raw string
          }
        }
        return trimmed;
      }

      // If API returns an object already
      if (typeof value === "object") {
        return (
          value?.[locale] ??
          value?.ar ??
          value?.en ??
          Object.values(value)[0] ??
          ""
        );
      }

      return String(value);
    },
    formatCurrency(value) {
      if (!value) {
        return this.getAppLocale === 'ar' ? '0 ر.س' : '0 SAR';
      }

      const isArabic = this.getAppLocale === 'ar';

      return new Intl.NumberFormat(isArabic ? 'ar-SA' : 'en-US', {
        style: 'currency',
        currency: 'SAR',
        minimumFractionDigits: 2,
      }).format(value);
    },

    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
    },
    async downloadExcel() {
      try {
        this.excelDownloadBtnIsLoading = true;

        const translatedData = (this.displayRows || []).map((row, index) => ({
          [this.$t("TABLES.Admins.serialNumber")]: row?.is_totals ? "-" : index + 1,
          [this.$t("PLACEHOLDERS.orderNum")]: row?.is_totals
            ? this.$t("PLACEHOLDERS.totals")
            : (row?.serial_number || "-"),
          [this.$t("PLACEHOLDERS.clientName")]:
            row?.is_totals ? "-" : (row?.user_name || "-"),
          [this.$t("SIDENAV.orders.created_at")]:
            row?.is_totals ? "-" : (row?.created_at || "-"),
          [this.$t("PLACEHOLDERS.order_type")]:
            row?.is_totals
              ? "-"
              : (row?.order_type === 'delivery'
                ? this.$t("PLACEHOLDERS.delivery")
                : row?.order_type === 'pickup'
                  ? this.$t("PLACEHOLDERS.pickup")
                  : (row?.order_type || "-")),
          [this.$t("PLACEHOLDERS.representative_profit")]:
            row?.representative_profit ?? 0,
          [this.$t("PLACEHOLDERS.print_shop_profit")]:
            row?.print_shop_profit ?? 0,
          [this.$t("PLACEHOLDERS.application_profit")]:
            row?.application_profit ?? 0,
          [this.$t("PLACEHOLDERS.total_profit")]: row?.total_profit ?? 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(translatedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          this.$t("PLACEHOLDERS.financial_reports")
        );

        XLSX.writeFile(
          workbook,
          `financial_reports_${new Date().toISOString().split("T")[0]}.xlsx`
        );

        this.$message.success(this.$t("MESSAGES.downloadStarted"));
      } catch (error) {
        this.$message.error(this.$t("MESSAGES.downloadFailed"));
        console.error("Download Excel error:", error);
      } finally {
        this.excelDownloadBtnIsLoading = false;
      }
    },

    updateRouterQueryParam(page) {
      this.paginations.current_page = page;
      this.setTableRows();
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    async submitFilterForm() {
      // Reset page to 1 when searching
      if (this.paginations.current_page !== 1) {
        this.paginations.current_page = 1;
        await this.$nextTick();
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.order_type = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;
      this.paginations.current_page = 1;
      this.setTableRows();
    },
    async setTableRows() {
      this.loading = true;
      try {
        const params = {
          page: this.paginations.current_page,
          limit: this.paginations.items_per_page,
        };

        // Add dates only if they exist
        if (this.filterOptions.from_date) {
          params.from = this.filterOptions.from_date;
        }
        if (this.filterOptions.to_date) {
          params.to = this.filterOptions.to_date;
        }
        if (this.filterOptions.order_type?.value) {
          params.order_type = this.filterOptions.order_type.value;
        } else if (typeof this.filterOptions.order_type === "string") {
          params.order_type = this.filterOptions.order_type;
        }

        let res = await this.$axios({
          method: "GET",
          url: "financial-reports",
          params: params,
        });
        const payload = res?.data?.data || {};
        this.tableRows = payload.data || [];
        this.paginations.current_page = payload.meta?.current_page || this.paginations.current_page;
        this.paginations.last_page = payload.meta?.last_page || 1;
        this.paginations.items_per_page = payload.meta?.per_page || this.paginations.items_per_page;
        this.totals.representative_profit = payload.totals?.representative_profit ?? 0;
        this.totals.print_shop_profit = payload.totals?.print_shop_profit ?? 0;
        this.totals.application_profit = payload.totals?.application_profit ?? 0;
        this.totals.total_profit = payload.totals?.total_profit ?? 0;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(error.response?.data?.message || 'حدث خطأ أثناء جلب البيانات');
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.setTableRows();
  },
};
</script>

<style scoped>
.ex-btn-s {
  color: black !important;
  font-size: 20px;
  font-weight: bold;
  text-decoration: underline !important;
}

.all-stat {
  background: var(--main_theme_clr);
}

.all-stat th {
  color: white !important;
  font-size: 16px !important;
  font-weight: 800;
}

.excel {
  background: darkgreen;
  padding: 7px 10px;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
}
</style>
